const FACEAPI_JS = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.js";
const FACEAPI_MODELS = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model";
const TESSERACT_JS = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";

const _loaded = {};
function loadScript(src) {
  if (_loaded[src]) return _loaded[src];
  _loaded[src] = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  return _loaded[src];
}

let _faceReady = null;
export async function ensureFaceEngine() {
  if (_faceReady) return _faceReady;
  _faceReady = (async () => {
    await loadScript(FACEAPI_JS);
    const faceapi = window.faceapi;
    await faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODELS);
    await faceapi.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODELS);
    await faceapi.nets.faceRecognitionNet.loadFromUri(FACEAPI_MODELS);
    return faceapi;
  })();
  return _faceReady;
}

export async function faceDescriptor(el) {
  const faceapi = await ensureFaceEngine();
  const det = await faceapi
    .detectSingleFace(el, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.4 }))
    .withFaceLandmarks()
    .withFaceDescriptor();
  return det || null;
}

export function faceMatchScore(d1, d2) {
  if (!d1 || !d2) return 0;
  const faceapi = window.faceapi;
  const dist = faceapi.euclideanDistance(d1, d2);
  const score = Math.round(Math.max(0, Math.min(1, 1 - (dist - 0.2) / 0.55)) * 100);
  return score;
}

function ear(eye) {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return (d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * d(eye[0], eye[3]));
}

export async function runLiveness(video, onProgress) {
  const faceapi = await ensureFaceEngine();
  const frames = 22;
  const gap = 150;
  let withFace = 0, minEar = 1, maxEar = 0, blink = false;
  const noseXs = [];
  let prevEar = null, dipThenRise = false;

  for (let i = 0; i < frames; i++) {
    const det = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }))
      .withFaceLandmarks();
    if (det) {
      withFace++;
      const lm = det.landmarks;
      const e = (ear(lm.getLeftEye()) + ear(lm.getRightEye())) / 2;
      minEar = Math.min(minEar, e);
      maxEar = Math.max(maxEar, e);
      if (prevEar !== null && prevEar < 0.2 && e > 0.25) dipThenRise = true;
      prevEar = e;
      const nose = lm.getNose()[3];
      if (nose) noseXs.push(nose.x);
    }
    if (onProgress) onProgress(Math.round(((i + 1) / frames) * 100));
    await new Promise((r) => setTimeout(r, gap));
  }

  blink = dipThenRise || (maxEar - minEar) > 0.12;
  const movement = noseXs.length ? Math.max(...noseXs) - Math.min(...noseXs) : 0;
  const faceRatio = withFace / frames;
  const moved = movement > 14;
  const passed = faceRatio >= 0.6 && (blink || moved);
  const score = Math.round(Math.min(100, faceRatio * 60 + (blink ? 25 : 0) + (moved ? 15 : 0)));
  return { passed, score, frames: withFace, blink, movement: Math.round(movement) };
}

let _tessReady = null;
async function ensureTesseract() {
  if (_tessReady) return _tessReady;
  _tessReady = loadScript(TESSERACT_JS).then(() => window.Tesseract);
  return _tessReady;
}

function checkDigit(str) {
  const w = [7, 3, 1];
  let sum = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    let v = 0;
    if (ch >= "0" && ch <= "9") v = ch.charCodeAt(0) - 48;
    else if (ch >= "A" && ch <= "Z") v = ch.charCodeAt(0) - 55;
    else v = 0;
    sum += v * w[i % 3];
  }
  return sum % 10;
}
const digit = (c) => (c === "<" || c === undefined ? -1 : c.charCodeAt(0) - 48);

export function parseMrz(rawText) {
  const lines = rawText
    .toUpperCase()
    .split(/\r?\n/)
    .map((l) => l.replace(/[^A-Z0-9<]/g, ""))
    .filter((l) => l.length >= 28 && /[<]/.test(l));

  const td3 = lines.filter((l) => l.length >= 40 && l.length <= 46);
  if (td3.length >= 2) {
    const l2 = td3[td3.length - 1].padEnd(44, "<").slice(0, 44);
    const l1 = td3[td3.length - 2].padEnd(44, "<").slice(0, 44);
    const docNo = l2.slice(0, 9);
    const docNoChk = digit(l2[9]);
    const nationality = l2.slice(10, 13).replace(/</g, "");
    const dob = l2.slice(13, 19);
    const dobChk = digit(l2[19]);
    const expiry = l2.slice(21, 27);
    const expChk = digit(l2[27]);
    const names = l1.slice(5).split("<<");
    const surname = (names[0] || "").replace(/</g, " ").trim();
    const given = (names[1] || "").replace(/</g, " ").trim();
    const checks = {
      doc_number: checkDigit(docNo) === docNoChk,
      dob: checkDigit(dob) === dobChk,
      expiry: checkDigit(expiry) === expChk,
    };
    const valid = checks.doc_number && checks.dob && checks.expiry;
    return {
      has_mrz: true, mrz_valid: valid, format: "TD3", checks,
      doc_type: "passport", doc_number: docNo.replace(/</g, ""),
      surname, given_names: given, nationality, dob: fmtDate(dob), expiry: fmtDate(expiry),
    };
  }

  const td1 = lines.filter((l) => l.length >= 28 && l.length <= 32);
  if (td1.length >= 3) {
    const a = td1[0].padEnd(30, "<");
    const b = td1[1].padEnd(30, "<");
    const c = td1[2].padEnd(30, "<");
    const docNo = a.slice(5, 14);
    const docNoChk = digit(a[14]);
    const dob = b.slice(0, 6);
    const dobChk = digit(b[6]);
    const expiry = b.slice(8, 14);
    const expChk = digit(b[14]);
    const nationality = b.slice(15, 18).replace(/</g, "");
    const names = c.split("<<");
    const surname = (names[0] || "").replace(/</g, " ").trim();
    const given = (names[1] || "").replace(/</g, " ").trim();
    const checks = {
      doc_number: checkDigit(docNo) === docNoChk,
      dob: checkDigit(dob) === dobChk,
      expiry: checkDigit(expiry) === expChk,
    };
    const valid = checks.doc_number && checks.dob && checks.expiry;
    return {
      has_mrz: true, mrz_valid: valid, format: "TD1", checks,
      doc_type: "national_id", doc_number: docNo.replace(/</g, ""),
      surname, given_names: given, nationality, dob: fmtDate(dob), expiry: fmtDate(expiry),
    };
  }
  return { has_mrz: false, mrz_valid: false };
}

function fmtDate(yymmdd) {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const year = yy > new Date().getFullYear() % 100 + 5 ? 1900 + yy : 2000 + yy;
  return `${year}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
}

function guessFromText(text) {
  const up = text.toUpperCase();
  const num = (up.match(/\b([A-Z]{1,3}\d{5,10}|\d{5,10}[A-Z]?)\b/) || [])[1] || "";
  const dl = (up.match(/(20\d{2}|19\d{2})[-/](\d{2})[-/](\d{2})/) || [])[0] || null;
  return { doc_number: num, expiry: dl };
}

export async function readDocument(imageEl) {
  const Tesseract = await ensureTesseract();
  const { data } = await Tesseract.recognize(imageEl, "eng");
  const text = data?.text || "";
  const ocr_confidence = Math.round(data?.confidence || 0);
  const mrz = parseMrz(text);
  if (mrz.has_mrz) {
    return {
      extracted: {
        doc_type: mrz.doc_type, doc_number: mrz.doc_number, surname: mrz.surname,
        given_names: mrz.given_names, nationality: mrz.nationality, dob: mrz.dob, expiry: mrz.expiry,
      },
      signals: { has_mrz: true, mrz_valid: mrz.mrz_valid, mrz_checks: mrz.checks, mrz_format: mrz.format, ocr_confidence, barcode_decoded: false },
      raw_text: text,
    };
  }
  const g = guessFromText(text);
  return {
    extracted: { doc_type: "drivers_license", doc_number: g.doc_number, surname: "", given_names: "", nationality: "", dob: null, expiry: g.expiry },
    signals: { has_mrz: false, mrz_valid: false, ocr_confidence, barcode_decoded: false },
    raw_text: text,
  };
}
